import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAward,
  faUpload,
  faPen,
  faXmark,
  faCircleCheck,
  faTriangleExclamation,
  faRotate,
  faTrashCan,
  faFloppyDisk,
  faImage,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';
import {
  FONTS,
  DATE_FORMATS,
  withDefaults,
  ensureFonts,
  loadTemplateImage,
  paintCertificate,
  makeCertificate,
} from '../components/Certificates/certificateMaker';
import './CertificateTemplates.css';

const API = process.env.REACT_APP_API_BASE_URL;
const BASE = `${API}/api/admin/certificate-templates`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const PLAN_LABEL = { all: 'Little & Junior (same design)', little: 'Little', junior: 'Junior' };

/* The editor of one design (a module, for everyone or for one level):
   the picture, click to place the name or the date, fonts and sizes */
const Editor = ({ mod, plan, planTabs, onClose, onSaved }) => {
  const design = mod.designs?.[plan] || null;
  const [url, setUrl] = useState(design?.url || null);
  const [config, setConfig] = useState(withDefaults(design?.config));
  const [which, setWhich] = useState('name');
  const [sample, setSample] = useState({ name: 'Karim Haddad', date: todayIso() });
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const canvasRef = useRef(null);
  const dragging = useRef(false);

  // load the picture + fonts
  useEffect(() => {
    let alive = true;
    if (!url) return undefined;
    (async () => {
      try {
        await ensureFonts(config);
        const i = await loadTemplateImage(mod.id, plan);
        if (alive) setImg(i);
      } catch (e) {
        if (alive) setMsg({ bad: true, text: e.message });
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // a new font: load it, then redraw
  const fontsKey = `${config.name.font}|${config.date.font}`;
  const [fontTick, setFontTick] = useState(0);
  useEffect(() => {
    let alive = true;
    ensureFonts(config).then(() => alive && setFontTick((n) => n + 1));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontsKey]);

  // redraw the preview
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const W = 1000;
    canvas.width = W;
    canvas.height = Math.round((W * img.naturalHeight) / img.naturalWidth);
    paintCertificate(canvas, img, config, sample);
    // the selected text's spot
    const t = config[which];
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#F7931E';
    ctx.setLineDash([8, 6]);
    ctx.lineWidth = 2;
    const h = Math.max(18, t.size * canvas.height * 1.3);
    const w = (which === 'name' ? t.maxWidth || 0.7 : 0.35) * canvas.width;
    const left = t.align === 'left' ? t.x * W : t.align === 'right' ? t.x * W - w : t.x * W - w / 2;
    ctx.strokeRect(left, t.y * canvas.height - h / 2, w, h);
  }, [img, config, sample, which, fontTick]);

  const set = (patch) => setConfig((c) => ({ ...c, [which]: { ...c[which], ...patch } }));

  const placeAt = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
    set({ x: Math.round(x * 1000) / 1000, y: Math.round(y * 1000) / 1000 });
  };

  const uploadPicture = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy('upload');
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('template', file);
      const res = await axios.post(`${BASE}/${mod.id}/${plan}/image`, fd, auth());
      setImg(null);
      setUrl(res.data.url);
      // a new picture: save the current placement with it
      await axios.put(`${BASE}/${mod.id}/${plan}/config`, { config }, auth());
      onSaved();
      setMsg({ text: 'Picture saved. Now place the name and the date.' });
    } catch (err) {
      setMsg({ bad: true, text: err.response?.data?.error || 'Couldn’t upload the picture.' });
    } finally {
      setBusy('');
    }
  };

  const save = async () => {
    setBusy('save');
    setMsg(null);
    try {
      await axios.put(`${BASE}/${mod.id}/${plan}/config`, { config }, auth());
      onSaved();
      setMsg({ text: 'Saved. Certificates for this module will use it.' });
    } catch (err) {
      setMsg({ bad: true, text: err.response?.data?.error || 'Couldn’t save.' });
    } finally {
      setBusy('');
    }
  };

  // download a test certificate (to check the PDF and the JPG)
  const test = async () => {
    setBusy('test');
    try {
      const out = await makeCertificate({ moduleId: mod.id, plan, config, name: sample.name, date: sample.date, fileBase: `test-${mod.name}` });
      [out.pdf, out.jpg].forEach((f) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(f);
        a.download = f.name;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      });
      URL.revokeObjectURL(out.previewUrl);
    } catch (err) {
      setMsg({ bad: true, text: err.message || 'Couldn’t make the test files.' });
    } finally {
      setBusy('');
    }
  };

  const removeDesign = async () => {
    if (!window.confirm(`Remove the ${PLAN_LABEL[plan]} design of ${mod.name}?`)) return;
    setBusy('remove');
    try {
      await axios.delete(`${BASE}/${mod.id}/${plan}`, auth());
      setUrl(null);
      setImg(null);
      onSaved();
      setMsg({ text: 'Design removed.' });
    } catch (err) {
      setMsg({ bad: true, text: err.response?.data?.error || 'Couldn’t remove it.' });
    } finally {
      setBusy('');
    }
  };

  const t = config[which];

  return (
    <div className="rmactp-overlay" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="rmactp-modal" role="dialog" aria-modal="true" aria-label={`Template: ${mod.name}`}>
        <div className="rmactp-modal-head">
          <h2>
            <FontAwesomeIcon icon={faAward} /> {mod.name}
          </h2>
          {planTabs}
          <button type="button" className="rmactp-x" onClick={onClose} disabled={!!busy} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="rmactp-editor">
          <div className="rmactp-stage">
            {url ? (
              img ? (
                <canvas
                  ref={canvasRef}
                  className="rmactp-canvas"
                  onMouseDown={(e) => {
                    dragging.current = true;
                    placeAt(e);
                  }}
                  onMouseMove={(e) => dragging.current && placeAt(e)}
                  onMouseUp={() => (dragging.current = false)}
                  onMouseLeave={() => (dragging.current = false)}
                  onClick={placeAt}
                />
              ) : (
                <div className="rmactp-loading">Loading the picture…</div>
              )
            ) : (
              <label className="rmactp-drop">
                <input type="file" accept="image/png,image/jpeg" onChange={uploadPicture} hidden />
                <FontAwesomeIcon icon={faUpload} />
                <b>{busy === 'upload' ? 'Uploading…' : 'Upload the Canva certificate'}</b>
                <small>In Canva: remove the name and the date, then Download › PNG (best quality).</small>
              </label>
            )}
            {url && <p className="rmactp-hint">Click or drag on the certificate to place the {which === 'name' ? 'name' : 'date'}.</p>}
          </div>

          <div className="rmactp-side">
            <div className="rmactp-seg" role="tablist">
              {[
                ['name', 'Name'],
                ['date', 'Date'],
              ].map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={which === k} className={which === k ? 'is-on' : ''} onClick={() => setWhich(k)}>
                  {l}
                </button>
              ))}
            </div>

            <label className="rmactp-field">
              <span>Font</span>
              <select value={t.font} onChange={(e) => set({ font: e.target.value })}>
                {FONTS.map((f) => (
                  <option key={f} value={f} style={{ fontFamily: f }}>
                    {f}
                  </option>
                ))}
              </select>
            </label>
            <label className="rmactp-field">
              <span>Size</span>
              <input type="range" min="0.01" max="0.16" step="0.002" value={t.size} onChange={(e) => set({ size: Number(e.target.value) })} />
            </label>
            <div className="rmactp-row">
              <label className="rmactp-field">
                <span>Colour</span>
                <input type="color" value={t.color} onChange={(e) => set({ color: e.target.value })} />
              </label>
              <label className="rmactp-field">
                <span>Weight</span>
                <select value={t.weight} onChange={(e) => set({ weight: Number(e.target.value) })}>
                  <option value={400}>Normal</option>
                  <option value={600}>Semi-bold</option>
                  <option value={700}>Bold</option>
                </select>
              </label>
              <label className="rmactp-field">
                <span>Align</span>
                <select value={t.align} onChange={(e) => set({ align: e.target.value })}>
                  <option value="center">Center</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
              </label>
            </div>
            {which === 'name' && (
              <label className="rmactp-field">
                <span>Space for long names</span>
                <input type="range" min="0.2" max="0.95" step="0.01" value={t.maxWidth} onChange={(e) => set({ maxWidth: Number(e.target.value) })} />
                <small>Longer names get smaller to fit the dashed box.</small>
              </label>
            )}
            {which === 'date' && (
              <label className="rmactp-field">
                <span>Date format</span>
                <select value={t.format} onChange={(e) => set({ format: e.target.value })}>
                  {DATE_FORMATS.map(([k, l]) => (
                    <option key={k} value={k}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="rmactp-sample">
              <span>Try it with</span>
              <input type="text" value={sample.name} onChange={(e) => setSample((s) => ({ ...s, name: e.target.value }))} placeholder="A long student name" />
              <input type="date" value={sample.date} onChange={(e) => setSample((s) => ({ ...s, date: e.target.value }))} />
            </div>

            {msg && (
              <p className={`rmactp-msg ${msg.bad ? 'is-bad' : ''}`} role="status">
                <FontAwesomeIcon icon={msg.bad ? faTriangleExclamation : faCircleCheck} /> {msg.text}
              </p>
            )}

            <div className="rmactp-actions">
              {url && (
                <label className="rmactp-btn rmactp-btn--ghost">
                  <input type="file" accept="image/png,image/jpeg" onChange={uploadPicture} hidden disabled={!!busy} />
                  <FontAwesomeIcon icon={faImage} /> {busy === 'upload' ? 'Uploading…' : 'Change picture'}
                </label>
              )}
              {url && img && (
                <button type="button" className="rmactp-btn rmactp-btn--ghost" onClick={test} disabled={!!busy}>
                  <FontAwesomeIcon icon={faDownload} /> {busy === 'test' ? 'Making…' : 'Test PDF + JPG'}
                </button>
              )}
              {url && (
                <button type="button" className="rmactp-btn rmactp-btn--ghost rmactp-btn--danger" onClick={removeDesign} disabled={!!busy}>
                  <FontAwesomeIcon icon={faTrashCan} /> Remove
                </button>
              )}
              <button type="button" className="rmactp-btn" onClick={save} disabled={!!busy || !url}>
                <FontAwesomeIcon icon={faFloppyDisk} /> {busy === 'save' ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* One module's editor with the design tabs: shared design, Little, Junior */
const EditorShell = ({ mod, onClose, onSaved }) => {
  const levels = mod.plans.length ? mod.plans : ['little', 'junior'];
  const first = mod.designs.all || !levels.some((p) => mod.designs[p]) ? 'all' : levels.find((p) => mod.designs[p]);
  const [plan, setPlan] = useState(first);
  const tabs = (
    <div className="rmactp-plans" role="tablist" aria-label="Design for">
      {['all', ...levels].map((p) => (
        <button key={p} type="button" role="tab" aria-selected={plan === p} className={`${plan === p ? 'is-on' : ''} ${mod.designs[p] ? 'has-design' : ''}`} onClick={() => setPlan(p)}>
          {p === 'all' ? 'Same for all' : `${PLAN_LABEL[p]} only`}
          {mod.designs[p] && <FontAwesomeIcon icon={faCircleCheck} />}
        </button>
      ))}
    </div>
  );
  return <Editor key={`${mod.id}-${plan}`} mod={mod} plan={plan} planTabs={tabs} onClose={onClose} onSaved={onSaved} />;
};

const designText = (m) => {
  if (m.noCertificate) return 'No certificate (part of another module)';
  const keys = Object.keys(m.designs);
  if (!keys.length) return 'No template yet';
  const ready = (k) => (m.designs[k]?.config ? '' : ' (place name & date)');
  return keys.map((k) => `${k === 'all' ? 'Same for all' : PLAN_LABEL[k]}${ready(k)}`).join(' · ');
};

/* Certificates › Templates tab (embedded) — also opens alone at /Admin/certificate-templates */
const CertificateTemplates = ({ embedded = false }) => {
  const [mods, setMods] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(BASE, auth());
      setMods(res.data?.modules || []);
    } catch (err) {
      setError(err.response?.data?.error || 'We couldn’t load the modules.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleNoCert = async (m) => {
    try {
      await axios.put(`${BASE}/${m.id}/settings`, { noCertificate: !m.noCertificate }, auth());
      load();
    } catch {
      /* the list stays as it is */
    }
  };

  const open = (mods || []).find((m) => m.id === openId) || null;
  const needed = (mods || []).filter((m) => !m.noCertificate);
  const ready = needed.filter((m) => Object.values(m.designs).some((d) => d?.config)).length;
  const thumb = (m) => m.designs.all?.url || m.designs.little?.url || m.designs.junior?.url || null;

  return (
    <div className={`rmactp ${embedded ? 'is-embedded' : ''}`}>
      <header className="rmactp-head">
        {!embedded && (
          <h1>
            <FontAwesomeIcon icon={faAward} /> Certificate templates
          </h1>
        )}
        <p>
          The Canva design of each module, without the name and the date. One design for everyone, or a different one for Little and Junior.
          Modules that are only a first part (like an introduction) can be set to “No certificate”.
        </p>
        {mods && (
          <p className="rmactp-count">
            {ready} of {needed.length} modules ready
          </p>
        )}
      </header>

      {error && !mods ? (
        <p className="rmactp-msg is-bad">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}{' '}
          <button type="button" className="rmactp-link" onClick={load}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </p>
      ) : !mods ? (
        <div className="rmactp-skel" aria-busy="true" />
      ) : (
        <ul className="rmactp-grid">
          {mods.map((m) => {
            const has = Object.values(m.designs).some((d) => d?.config);
            return (
              <li key={m.id} className={`rmactp-card ${m.noCertificate ? 'is-off' : has ? 'is-ready' : ''}`}>
                <button type="button" className="rmactp-thumb" onClick={() => setOpenId(m.id)} aria-label={`Edit the templates of ${m.name}`} disabled={m.noCertificate}>
                  {thumb(m) ? <img src={thumb(m)} alt="" loading="lazy" /> : <FontAwesomeIcon icon={faUpload} />}
                </button>
                <div className="rmactp-card-foot">
                  <b>{m.name}</b>
                  <div className="rmactp-levels">
                    {(m.plans.length ? m.plans : []).map((p) => (
                      <i key={p} className={`is-${p}`}>
                        {PLAN_LABEL[p]}
                      </i>
                    ))}
                  </div>
                  <span>{designText(m)}</span>
                  <div className="rmactp-card-actions">
                    {!m.noCertificate && (
                      <button type="button" onClick={() => setOpenId(m.id)} aria-label="Edit">
                        <FontAwesomeIcon icon={Object.keys(m.designs).length ? faPen : faUpload} />
                      </button>
                    )}
                  </div>
                  <label className="rmactp-nocert">
                    <input type="checkbox" checked={m.noCertificate} onChange={() => toggleNoCert(m)} />
                    No certificate for this module
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {open && <EditorShell key={open.id} mod={open} onClose={() => setOpenId(null)} onSaved={load} />}
    </div>
  );
};

export default CertificateTemplates;
